import type { operations } from '@molchanovs/api-contract';
import { z } from 'zod';
import { api } from '@shared/api';

type AiResponse =
  operations['postApiV1ApplicationAiResponses']['responses'][201]['content']['application/json'];
export type FaqPreview = Extract<AiResponse['data'], { useCase: 'helpdesk_faq_preview' }>;
type ModuleResponse =
  operations['getApiV1EducationModules']['responses'][200]['content']['application/json'];
type OperatorResponse = Extract<ModuleResponse, { meta: { hasNextPage: boolean } }>;
type SaveResponse =
  operations['putApiV1EducationModulesByModuleIdFaq']['responses'][200]['content']['application/json'];

const faqContextSchema = z.object({
  audience: z.string().nullable(),
  purpose: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
const moduleSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  status: z.string().nullable(),
  type: z.string().nullable(),
  cover: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('image'), url: z.url() }),
    z.object({ kind: z.literal('placeholder') }),
  ]),
  faq: faqContextSchema,
});
export type FaqModule = z.infer<typeof moduleSchema>;
export const faqModulesSchema = z.object({
  data: z.object({ items: z.array(moduleSchema) }),
  meta: z.object({
    hasNextPage: z.boolean(),
    limit: z.number().int().positive(),
    nextCursor: z.string().nullable(),
  }),
}) satisfies z.ZodType<OperatorResponse>;
export const faqPreviewSchema: z.ZodType<{ data: FaqPreview }> = z.object({
  data: z.object({
    id: z.uuid(),
    useCase: z.literal('helpdesk_faq_preview'),
    state: z.literal('succeeded'),
    output: z.object({
      moduleId: z.uuid(),
      audience: z.string().nullable(),
      basisRevision: z.string().min(1),
      pairs: z
        .array(
          z.object({
            question: z
              .string()
              .min(1)
              .refine((s) => s.trim().length > 0 && !s.includes('\0')),
            answer: z
              .string()
              .min(1)
              .refine((s) => s.trim().length > 0 && !s.includes('\0')),
          })
        )
        .min(10),
      outputSha256: z.string().regex(/^[a-f0-9]{64}$/),
      saved: z.literal(false),
    }),
  }),
});
export const faqSaveSchema = z.object({
  data: z.object({ moduleId: z.uuid(), previewId: z.uuid() }),
}) satisfies z.ZodType<SaveResponse>;

// The existing cursor contract bounds each page, not the eligible collection.
export async function getFaqModules(signal?: AbortSignal): Promise<FaqModule[]> {
  const items: FaqModule[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  for (;;) {
    const page = await api.get('/api/v1/education-modules', {
      params: { view: 'operator', faqEligible: true, cursor },
      signal,
      schema: faqModulesSchema,
    });
    items.push(...page.data.items);
    if (!page.meta.hasNextPage) return items;
    const next = page.meta.nextCursor;
    if (!next || seen.has(next)) throw new Error('Invalid Education Module cursor');
    seen.add(next);
    cursor = next;
  }
}

export async function generateFaq(moduleId: string, key: string): Promise<FaqPreview> {
  const response = await api.post(
    '/api/v1/application-ai-responses',
    {
      useCase: 'helpdesk_faq_preview',
      input: { moduleId: z.uuid().parse(moduleId) },
    },
    {
      headers: { 'Idempotency-Key': key },
      schema: faqPreviewSchema,
      skipAuthRefresh: true,
      // Knowledge lookup and generation can outlast the ordinary 30-second API request.
      timeout: 300_000,
    }
  );
  if (response.data.output.moduleId !== moduleId) throw new Error('Wrong FAQ preview target');
  return response.data;
}

export async function saveFaq(preview: FaqPreview, key: string): Promise<void> {
  const moduleId = z.uuid().parse(preview.output.moduleId);
  const previewId = z.uuid().parse(preview.id);
  const response = await api.put(
    `/api/v1/education-modules/${moduleId}/faq`,
    { previewId },
    {
      headers: { 'Idempotency-Key': key, 'If-Match': preview.output.basisRevision },
      schema: faqSaveSchema,
      skipAuthRefresh: true,
    }
  );
  if (response.data.moduleId !== moduleId || response.data.previewId !== previewId)
    throw new Error('Wrong FAQ save receipt');
}
